import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {AbortController | undefined} */
  #cartRequest;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('rebuy:cart.ready', this.onCartUpdate);
    document.addEventListener('rebuy:cart.change', this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('rebuy:cart.ready', this.onCartUpdate);
    document.removeEventListener('rebuy:cart.change', this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#cartRequest?.abort();
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CustomEvent<{ resource?: { item_count?: number }, cart?: { cart?: { item_count?: number } } }>} event
   */
  onCartUpdate = (event) => {
    const itemCount = event.detail?.resource?.item_count ?? event.detail?.cart?.cart?.item_count;

    if (typeof itemCount === 'number' && Number.isInteger(itemCount) && itemCount >= 0) {
      this.renderCartBubble(itemCount);
    } else {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} [animate] - Whether to animate the updated count.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.#cartRequest?.abort();

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.refs.cartBubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    this.dispatchEvent(new CustomEvent('cart-icon:update', { bubbles: true, detail: { itemCount } }));

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    this.#cartRequest?.abort();
    const controller = new AbortController();
    this.#cartRequest = controller;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        signal: controller.signal,
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

      const cart = await response.json();
      if (!controller.signal.aborted && this.isConnected && Number.isInteger(cart.item_count) && cart.item_count >= 0) {
        this.renderCartBubble(cart.item_count, false);
      }
    } catch (error) {
      if (!controller.signal.aborted) console.error('Failed to update cart count:', error);
    } finally {
      if (this.#cartRequest === controller) this.#cartRequest = undefined;
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
